import { stripValidationErrorPrefix } from '@/features/extrajudicial/utils/stripValidationErrorPrefix';
import type { ItemDetalheNotificacaoResponse } from '../types/documentoNotificacao.types';
import type { CorrecaoNomesLoteResult } from '@/shared/types/correcaoNomes';
import { parseSpreadsheetValidationError } from '@/shared/utils/parseSpreadsheetValidationError';
import { apiGet, apiPatch, apiPost, apiPut, downloadBlob } from '@/shared/utils/requestBuilder';

import type {
  AjusteCanal,
  AjusteCanalDTO,
  AjusteItem,
  AjusteItemDTO,
  AjusteLoteGrupo,
  AjusteLoteGrupoDTO,
  CorrecaoContato,
  ReenviarAjusteResult,
} from '../types/ajustes.types';
import { MensagemCanal } from '../types/common.types';

import type {
  BatchDashboardResponse,
  ChannelDashboardResponse,
  ContatoData,
  DashboardFilters,
  EnderecoData,
  GeneralDashboardResponse,
  ManifestationDashboardResponse,
  EnvioDigitalSnapshotOutput,
  ItemEditData,
  ItemFilters,
  ItemUpdateInput,
  ItemUpdateResult,
  ItemWithRelations,
  LoteCustos,
  LoteFilters,
  LoteOutput,
  LoteStats,
  LoteTabCounts,
  ManifestacaoMensagem,
  ManifestacaoOutput,
  NotificacaoFilters,
  NotificacaoListItem,
  NotificationResponseOptionInput,
  RecomendacaoIA,
  SendDigitalInput,
  SendFisicoInput,
  SendMethod,
  StartNegociacaoBulkResult,
  TemplateItem,
  ValidationErrorSample,
  ValoresEfetivos,
} from '@/features/extrajudicial/types';
import type { PaginatedResponse } from '@/features/extrajudicial/types/common.types';
import type {
  LoteConfigInput,
  LoteDetailResponse,
  LoteDraftOutput,
  LoteListResponse,
  LotePerformanceDetailedResponse,
  MapeamentoColunas,
} from '@/features/extrajudicial/types/lote.types';
import type {
  ConsultaDaManifestacaoChat,
  ManifestacaoAnexo,
  ManifestacaoDirecao,
  ManifestacaoTipo,
  OpcaoEscolhidaDaResposta,
} from '@/features/extrajudicial/types/manifestacao.types';
import type {
  ErroDaConsultaDaManifestacao,
  LeituraDaMensagemDaManifestacao,
  LeituraDaPaginaDaManifestacao,
  MensagemForaDoContrato,
} from '@/features/extrajudicial/types/manifestacaoChatUi.types';
import {
  lerOrigemDoContato,
  textoPreenchido,
} from '@/features/extrajudicial/utils/origemDoContato';
import { isRecord, parseApiResponse } from '@/shared/utils/apiResponse';

interface UploadResponse {
  loteId: string;
}

interface CriarIndividualResponse {
  loteId: string;
  itemId: string;
  numeroNotificacao: string | null;
}

interface UploadErrorPayload {
  message: string;
  error?: string;
  errorsSample?: ValidationErrorSample[];
}

interface RemoveItemFromValidationResponse {
  lote: LoteOutput;
  removedItem: {
    id: string;
    loteId: string;
    linhaNumero: number | null;
    numeroNotificacao: string | null;
    classificacao: string;
    erros: unknown;
    contato: unknown;
    endereco: unknown;
    metadata: unknown;
  };
}

interface RawItemWithManifestacoes extends Omit<ItemDetalheNotificacaoResponse, 'manifestacoes'> {
  extrajudicialManifestacoes?: ManifestacaoOutput[];
  manifestacoes?: ManifestacaoOutput[];
}

const mapItemManifestacoes = (raw: RawItemWithManifestacoes): ItemDetalheNotificacaoResponse => {
  const { extrajudicialManifestacoes, manifestacoes: rawManifestacoes, ...rest } = raw;
  return {
    ...rest,
    manifestacoes: extrajudicialManifestacoes ?? rawManifestacoes ?? [],
  };
};

const mapAjusteCanal = (canal: AjusteCanalDTO): AjusteCanal => ({
  envioId: canal.envioId ?? '',
  canal: canal.canal ?? MensagemCanal.EMAIL,
  contatoAtual: canal.contatoAtual ?? null,
  motivo: canal.motivo ?? '',
  contatoCorrigido: canal.contatoCorrigido === true,
  ...(canal.didYouMean ? { didYouMean: canal.didYouMean } : {}),
});

const mapAjusteItem = (item: AjusteItemDTO): AjusteItem => ({
  itemId: item.itemId ?? '',
  nome: item.nome ?? '',
  ignorado: item.ignorado === true,
  canais: (item.canais ?? []).map(mapAjusteCanal),
});

const mapAjusteGrupos = (grupos: AjusteLoteGrupoDTO[] | undefined | null): AjusteLoteGrupo[] =>
  (grupos ?? []).map((grupo) => ({
    loteId: grupo.loteId ?? '',
    batchName: grupo.batchName ?? '',
    batchCode: grupo.batchCode ?? null,
    casos: grupo.casos ?? 0,
    itens: (grupo.itens ?? []).map(mapAjusteItem),
  }));

const handleApiError = async (response: Response, defaultMsg: string): Promise<never> => {
  const body = await response.json().catch(() => ({ error: defaultMsg }));
  const rawMsg =
    (typeof body?.error === 'string' ? body.error : body?.error?.message) ||
    body?.message ||
    defaultMsg;
  const errMsg = stripValidationErrorPrefix(rawMsg);
  if (response.status === 403) {
    throw new Error(`FORBIDDEN:${errMsg}`);
  }
  throw new Error(errMsg);
};

const buildDashboardParams = (filters?: DashboardFilters) => {
  const params = new URLSearchParams();

  if (!filters) return params;

  if (filters.batches?.length) {
    filters.batches.forEach((b) => params.append('batches', b));
  }

  if (filters.createdBy?.length) {
    filters.createdBy.forEach((c) => params.append('createdBy', c));
  }

  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);

  if (filters.hasManifestation !== undefined) {
    params.append('hasManifestation', String(filters.hasManifestation));
  }

  if (filters.channel) params.append('channel', filters.channel);

  return params;
};

export const MENSAGEM_DE_FALHA_DA_MANIFESTACAO = 'Erro ao obter mensagens da manifestação';

/** Status zero indica rede ou contrato inválido, sem resposta HTTP de recusa. */
export class FalhaDaConsultaDaManifestacao extends Error {
  readonly erro: ErroDaConsultaDaManifestacao;

  constructor(status: number) {
    super(MENSAGEM_DE_FALHA_DA_MANIFESTACAO);
    this.name = 'FalhaDaConsultaDaManifestacao';
    this.erro = { status, mensagem: MENSAGEM_DE_FALHA_DA_MANIFESTACAO };
  }
}

/** Resposta do chat que não segue o contrato do core: falha de leitura, e não chat vazio. */
export class ManifestacaoForaDoContrato extends FalhaDaConsultaDaManifestacao {
  constructor() {
    super(0);
    this.name = 'ManifestacaoForaDoContrato';
  }
}

const TIPOS_DE_MANIFESTACAO: ReadonlySet<string> = new Set<ManifestacaoTipo>([
  'MANIFESTACAO',
  'RESPOSTA',
  'COMPROVANTE',
  'DISCORDANCIA',
  'NEGOCIACAO_REQUEST',
  'RESPOSTA_SIMPLES',
]);

const ehTipoDeManifestacao = (valor: unknown): valor is ManifestacaoTipo =>
  typeof valor === 'string' && TIPOS_DE_MANIFESTACAO.has(valor);

const ehDirecao = (valor: unknown): valor is ManifestacaoDirecao =>
  valor === 'INBOUND' || valor === 'OUTBOUND';

const textoOuNulo = (valor: unknown): string | null => (typeof valor === 'string' ? valor : null);

const numeroInteiro = (valor: unknown): valor is number =>
  typeof valor === 'number' && Number.isInteger(valor);

/**
 * O download é pela posição do anexo na mensagem: descartar um anexo malformado deslocaria os
 * seguintes e baixaria o arquivo errado. Por isso todo anexo fica, mesmo sem nome.
 */
const lerAnexo = (valor: unknown): ManifestacaoAnexo => {
  const anexo = isRecord(valor) ? valor : {};
  return {
    name: textoOuNulo(anexo.name) ?? '',
    path: textoOuNulo(anexo.path) ?? '',
    type: textoOuNulo(anexo.type) ?? '',
  };
};

const lerOpcaoEscolhida = (valor: unknown): OpcaoEscolhidaDaResposta | null =>
  isRecord(valor) && textoPreenchido(valor.id) && textoPreenchido(valor.rotulo)
    ? { id: valor.id, rotulo: valor.rotulo }
    : null;

/**
 * Origem e opção fora do contrato viram `null` ("Origem não identificada", sem rótulo) e são
 * devolvidas para registro: nunca se atribui um contato que não veio no formato da prova, e uma
 * mensagem com campo quebrado não derruba o chat. OUTBOUND é do escritório e não tem origem de
 * resposta recebida, então as duas são sempre `null` nele.
 */
function lerAtribuicoesDaMensagem(
  valor: Record<string, unknown>,
  mensagemId: string,
  direcao: ManifestacaoDirecao,
  foraDoContrato: MensagemForaDoContrato[],
): Pick<ManifestacaoMensagem, 'origem' | 'opcaoEscolhida'> {
  if (direcao === 'OUTBOUND') {
    if (valor.origem != null) foraDoContrato.push({ mensagemId, campo: 'origem' });
    if (valor.opcaoEscolhida != null) foraDoContrato.push({ mensagemId, campo: 'opcaoEscolhida' });
    return { origem: null, opcaoEscolhida: null };
  }
  const origem = valor.origem === null ? null : lerOrigemDoContato(valor.origem);
  if (valor.origem !== null && !origem) foraDoContrato.push({ mensagemId, campo: 'origem' });
  const opcaoEscolhida =
    valor.opcaoEscolhida === null ? null : lerOpcaoEscolhida(valor.opcaoEscolhida);
  if (valor.opcaoEscolhida !== null && !opcaoEscolhida) {
    foraDoContrato.push({ mensagemId, campo: 'opcaoEscolhida' });
  }
  return { origem, opcaoEscolhida };
}

/** Reconstrói a mensagem só com os campos do contrato: nada além disso chega ao estado da tela. */
function lerMensagemDaManifestacao(
  valor: unknown,
  foraDoContrato: MensagemForaDoContrato[],
): ManifestacaoMensagem {
  if (
    !isRecord(valor) ||
    !textoPreenchido(valor.id) ||
    !ehDirecao(valor.direcao) ||
    !ehTipoDeManifestacao(valor.tipo) ||
    !textoPreenchido(valor.dataCriacao)
  ) {
    throw new ManifestacaoForaDoContrato();
  }
  const autor = isRecord(valor.autor) ? { nome: textoOuNulo(valor.autor.nome) } : null;
  return {
    id: valor.id,
    itemId: textoOuNulo(valor.itemId) ?? '',
    usuarioId: textoOuNulo(valor.usuarioId) ?? '',
    tipo: valor.tipo,
    direcao: valor.direcao,
    conteudo: textoOuNulo(valor.conteudo),
    lido: valor.lido === true,
    lidoAt: textoOuNulo(valor.lidoAt),
    dataCriacao: valor.dataCriacao,
    autor,
    anexos: Array.isArray(valor.anexos) ? valor.anexos.map(lerAnexo) : [],
    ...lerAtribuicoesDaMensagem(valor, valor.id, valor.direcao, foraDoContrato),
  };
}

/**
 * Lê `items`, e não `mensagens`: as duas são a mesma página, e `items` é o campo do contrato
 * paginado. Sem os campos de paginação a resposta é de um core sem paginação, que manda a thread
 * inteira: tratá-la como página 1 esconderia o "Carregar mensagens anteriores" errado.
 */
function lerPaginaDaManifestacao(valor: unknown): LeituraDaPaginaDaManifestacao {
  if (
    !isRecord(valor) ||
    !Array.isArray(valor.items) ||
    !numeroInteiro(valor.total) ||
    !numeroInteiro(valor.page) ||
    !numeroInteiro(valor.pageSize) ||
    !numeroInteiro(valor.totalPages) ||
    typeof valor.hasNext !== 'boolean' ||
    typeof valor.hasPrevious !== 'boolean' ||
    !numeroInteiro(valor.respostasSimplesSent) ||
    !numeroInteiro(valor.limiteRespostasSimples) ||
    typeof valor.podeResponder !== 'boolean' ||
    typeof valor.temNegociacaoAtiva !== 'boolean'
  ) {
    throw new ManifestacaoForaDoContrato();
  }
  const foraDoContrato: MensagemForaDoContrato[] = [];
  const mensagens = valor.items.map((item) => lerMensagemDaManifestacao(item, foraDoContrato));
  return {
    pagina: {
      items: mensagens,
      mensagens,
      total: valor.total,
      page: valor.page,
      pageSize: valor.pageSize,
      totalPages: valor.totalPages,
      hasNext: valor.hasNext,
      hasPrevious: valor.hasPrevious,
      respostasSimplesSent: valor.respostasSimplesSent,
      limiteRespostasSimples: valor.limiteRespostasSimples,
      podeResponder: valor.podeResponder,
      temNegociacaoAtiva: valor.temNegociacaoAtiva,
    },
    foraDoContrato,
  };
}

const lerCorpoDaManifestacao = async (response: Response): Promise<unknown> =>
  parseApiResponse<unknown>(response).catch(() => {
    throw new ManifestacaoForaDoContrato();
  });

export const extrajudicialService = {
  listarTemplates: async (): Promise<TemplateItem[]> => {
    const response = await apiGet('/extrajudicial/templates');
    if (!response.ok) {
      await handleApiError(response, 'Erro ao listar templates');
    }
    const body = await response.json();
    const payload = body?.data ?? body;
    const data = (payload.items ?? payload) as Array<{
      id: string;
      nome: string;
      tipo?: 'BASE' | 'CUSTOM' | 'FLOW';
      status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
      whatsappStatus?: string | null;
      versaoPublicada?: number | null;
      canal?: string;
      titulo?: string | null;
      descricao?: string | null;
      variables?: Array<{
        key: string;
        required?: boolean;
        descricao?: string | null;
        exemplo?: string | null;
      }>;
    }>;

    return data.map((t) => ({
      id: t.id,
      name: t.nome ?? t.titulo ?? t.id,
      description: t.titulo ?? t.descricao ?? null,
      tipo: t.tipo,
      status: t.status,
      whatsappStatus: t.whatsappStatus ?? null,
      versaoPublicada: t.versaoPublicada ?? null,
      canal: t.canal,
      variables: t.variables?.map((v) => ({
        key: v.key,
        required: v.required,
        descricao: v.descricao || undefined,
        exemplo: v.exemplo || undefined,
      })),
    }));
  },

  baixarModeloCsv: async (input: {
    sendMethod: SendMethod;
    whatsappTemplateId?: string;
    emailTemplateId?: string;
    fisicoTemplateId?: string;
  }): Promise<void> => {
    const { sendMethod, whatsappTemplateId, emailTemplateId, fisicoTemplateId } = input;
    const filename = `modelo_${sendMethod}.csv`;
    const queryParams = new URLSearchParams({ sendMethod });
    if (whatsappTemplateId) queryParams.append('whatsappTemplateId', whatsappTemplateId);
    if (emailTemplateId) queryParams.append('emailTemplateId', emailTemplateId);
    if (fisicoTemplateId) queryParams.append('fisicoTemplateId', fisicoTemplateId);
    await downloadBlob(
      `/extrajudicial/notificacoes/lotes/template?${queryParams.toString()}`,
      filename,
    );
  },

  criarLoteRascunho: async (): Promise<LoteDraftOutput> => {
    const response = await apiPost('/extrajudicial/notificacoes/lotes/rascunho', {});
    if (!response.ok) {
      await handleApiError(response, 'Erro ao criar lote rascunho');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  removerItemDaValidacao: async (
    loteId: string,
    itemId: string,
  ): Promise<RemoveItemFromValidationResponse> => {
    const response = await apiPost(
      `/extrajudicial/notificacoes/lotes/${loteId}/itens/${itemId}/remover`,
      {},
    );

    if (!response.ok) {
      await handleApiError(response, 'Erro ao remover item do lote');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  corrigirNomesEmLote: async (loteId: string): Promise<CorrecaoNomesLoteResult> => {
    const response = await apiPost(
      `/extrajudicial/notificacoes/lotes/${loteId}/corrigir-nomes`,
      {},
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao corrigir nomes em lote');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  atualizarConfiguracaoLote: async (loteId: string, data: LoteConfigInput): Promise<LoteOutput> => {
    const response = await apiPatch(
      `/extrajudicial/notificacoes/lotes/${loteId}/configuracao`,
      data,
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao atualizar configuração');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  avancarConfiguracaoLote: async (
    loteId: string,
    data: {
      batchName: string;
      sendMethod: string;
      whatsappTemplateId?: string;
      emailTemplateId?: string;
      fisicoTemplateId?: string;
    },
  ): Promise<LoteOutput> => {
    const response = await apiPost(
      `/extrajudicial/notificacoes/lotes/${loteId}/configuracao/avancar`,
      data,
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao avançar configuração');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  cancelarConfiguracaoLote: async (loteId: string): Promise<void> => {
    const response = await apiPost(
      `/extrajudicial/notificacoes/lotes/${loteId}/configuracao/cancelar`,
      {},
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao cancelar configuração');
    }
  },

  obterMetodosPermitidos: async (): Promise<string[]> => {
    const response = await apiGet('/extrajudicial/notificacoes/lotes/metodos-permitidos');
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter métodos permitidos');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  criarLote: async (params: {
    file: File;
    sendMethod: SendMethod;
    batchName: string;
    batchCode?: string;
    loteId?: string;
    whatsappTemplateId?: string;
    emailTemplateId?: string;
    fisicoTemplateId?: string;
    prazoDias?: number;
    ignoreRows?: number[];
  }): Promise<UploadResponse> => {
    const form = new FormData();
    form.append('file', params.file);
    form.append('sendMethod', params.sendMethod);
    if (params.whatsappTemplateId) form.append('whatsappTemplateId', params.whatsappTemplateId);
    if (params.emailTemplateId) form.append('emailTemplateId', params.emailTemplateId);
    if (params.fisicoTemplateId) form.append('fisicoTemplateId', params.fisicoTemplateId);
    form.append('batchName', params.batchName);
    if (params.batchCode) form.append('batchCode', params.batchCode);
    if (params.loteId) form.append('loteId', params.loteId);
    if (typeof params.prazoDias === 'number') {
      form.append('prazoDias', String(params.prazoDias));
    }
    if (params.ignoreRows && params.ignoreRows.length > 0) {
      form.append('ignoreRows', JSON.stringify(params.ignoreRows));
    }

    const response = await apiPost('/extrajudicial/notificacoes/lotes', form);

    if (response.status === 201 || response.ok) {
      const body = await response.json().catch(() => ({}));
      const data = body?.data ?? body;
      if (!data?.loteId) throw new Error('Resposta inválida do servidor');
      return { loteId: data.loteId };
    }

    const validationError = await parseSpreadsheetValidationError(response);
    if (validationError) throw validationError;

    let errorBody: Record<string, unknown> | undefined;
    try {
      errorBody = (await response.json()) as Record<string, unknown>;
    } catch {}

    const error: UploadErrorPayload | undefined =
      errorBody && typeof errorBody === 'object'
        ? ((errorBody.data as UploadErrorPayload | undefined) ??
          (errorBody as unknown as UploadErrorPayload))
        : undefined;

    const message = error?.message || error?.error || 'Falha no envio do arquivo';
    const e = new Error(message) as Error & { errorsSample?: ValidationErrorSample[] };
    if (error?.errorsSample) e.errorsSample = error.errorsSample;
    throw e;
  },

  criarIndividual: async (params: {
    sendMethod: SendMethod;
    whatsappTemplateId?: string;
    emailTemplateId?: string;
    fisicoTemplateId?: string;
    breveRelato: string;
    valor: number;
    contato?: ContatoData;
    endereco?: EnderecoData;
    metadata?: Record<string, string>;
    margemNegociacao?: number;
    requerentePessoaId?: string;
    requeridoPessoaId?: string;
    prazoDias?: number;
    responseOptions?: NotificationResponseOptionInput[];
  }): Promise<CriarIndividualResponse> => {
    const payload = {
      sendMethod: params.sendMethod,
      whatsappTemplateId: params.whatsappTemplateId,
      emailTemplateId: params.emailTemplateId,
      fisicoTemplateId: params.fisicoTemplateId,
      breveRelato: params.breveRelato,
      valor: params.valor,
      contato: params.contato,
      endereco: params.endereco,
      metadata: params.metadata,
      margemNegociacao: params.margemNegociacao,
      requerentePessoaId: params.requerentePessoaId,
      requeridoPessoaId: params.requeridoPessoaId,
      prazoDias: params.prazoDias,
      responseOptions: params.responseOptions,
    };

    const response = await apiPost('/extrajudicial/notificacoes/individuais', payload);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao criar notificação individual');
    }

    const body = await response.json().catch(() => ({}));
    const data = body?.data ?? body;
    const loteId = data?.lote?.id ?? data?.loteId;
    const itemId = data?.item?.id ?? data?.itemId;
    const numeroNotificacao: string | null =
      data?.item?.numeroNotificacao ?? data?.numeroNotificacao ?? null;

    if (!loteId || !itemId) {
      throw new Error('Resposta inválida do servidor');
    }

    return { loteId, itemId, numeroNotificacao };
  },

  criarLoteComMapeamento: async (params: {
    file: File;
    sendMethod: SendMethod;
    batchName: string;
    batchCode?: string;
    loteId?: string;
    mapeamento: MapeamentoColunas;
    whatsappTemplateId?: string;
    emailTemplateId?: string;
    fisicoTemplateId?: string;
    prazoDias?: number;
    ignoreRows?: number[];
  }): Promise<{ loteId: string }> => {
    const form = new FormData();
    form.append('file', params.file);
    form.append('sendMethod', params.sendMethod);
    if (params.whatsappTemplateId) form.append('whatsappTemplateId', params.whatsappTemplateId);
    if (params.emailTemplateId) form.append('emailTemplateId', params.emailTemplateId);
    if (params.fisicoTemplateId) form.append('fisicoTemplateId', params.fisicoTemplateId);
    form.append('batchName', params.batchName);
    form.append('mapeamento', JSON.stringify(params.mapeamento));
    if (params.batchCode) form.append('batchCode', params.batchCode);
    if (params.loteId) form.append('loteId', params.loteId);
    if (typeof params.prazoDias === 'number') {
      form.append('prazoDias', String(params.prazoDias));
    }
    if (params.ignoreRows && params.ignoreRows.length > 0) {
      form.append('ignoreRows', JSON.stringify(params.ignoreRows));
    }

    const response = await apiPost('/extrajudicial/notificacoes/lotes/mapeamento', form);

    if (response.status === 201 || response.ok) {
      const body = await response.json().catch(() => ({}));
      const data = body?.data ?? body;
      if (!data?.loteId) throw new Error('Resposta inválida do servidor');
      return { loteId: data.loteId };
    }

    const validationError = await parseSpreadsheetValidationError(response);
    if (validationError) throw validationError;

    let errorBody: Record<string, unknown> | undefined;
    try {
      errorBody = (await response.json()) as Record<string, unknown>;
    } catch {}

    const error: UploadErrorPayload | undefined =
      errorBody && typeof errorBody === 'object'
        ? ((errorBody.data as UploadErrorPayload | undefined) ??
          (errorBody as unknown as UploadErrorPayload))
        : undefined;

    const message = error?.message || error?.error || 'Falha no envio do arquivo';
    const e = new Error(message) as Error & { errorsSample?: ValidationErrorSample[] };
    if (error?.errorsSample) e.errorsSample = error.errorsSample;
    throw e;
  },

  listarLotes: async (filters: LoteFilters): Promise<LoteListResponse> => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.sendMethod) params.append('sendMethod', filters.sendMethod);
    if (filters.batchName) params.append('batchName', filters.batchName);
    if (filters.search) params.append('search', filters.search);
    if (filters.includeDashboard) params.append('includeDashboard', 'true');
    if (filters.emRascunho) params.append('emRascunho', 'true');
    if (filters.dataInicio) params.append('dataInicio', filters.dataInicio);
    if (filters.dataFim) params.append('dataFim', filters.dataFim);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize));
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const url = `/extrajudicial/notificacoes/lotes${params.toString() ? `?${params}` : ''}`;
    const response = await apiGet(url);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao listar lotes');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  obterLote: async (id: string, page?: number, limit?: number): Promise<LoteDetailResponse> => {
    const params = new URLSearchParams();
    if (page) params.append('page', String(page));
    if (limit) params.append('limit', String(limit));

    const url = `/extrajudicial/notificacoes/lotes/${id}${params.toString() ? `?${params}` : ''}`;
    const response = await apiGet(url);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter lote');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  obterLoteSummary: async (id: string): Promise<{ lote: LoteOutput }> => {
    const response = await apiGet(`/extrajudicial/notificacoes/lotes/${id}?view=summary`);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter lote');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  obterEstatisticas: async (): Promise<LoteStats> => {
    const response = await apiGet('/extrajudicial/notificacoes/lotes/stats');
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter estatísticas');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  listarItensDoLote: async (
    loteId: string,
    filters: ItemFilters,
  ): Promise<PaginatedResponse<ItemWithRelations>> => {
    const params = new URLSearchParams();
    if (filters.classificacao) params.append('classificacao', filters.classificacao);
    if (filters.valorMin) params.append('valorMin', String(filters.valorMin));
    if (filters.valorMax) params.append('valorMax', String(filters.valorMax));
    if (filters.page) params.append('page', String(filters.page));
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize));
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const url = `/extrajudicial/notificacoes/lotes/${loteId}/itens${params.toString() ? `?${params}` : ''}`;
    const response = await apiGet(url);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao listar itens do lote');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  listarNotificacoes: async (
    filters: NotificacaoFilters,
  ): Promise<PaginatedResponse<NotificacaoListItem>> => {
    const params = new URLSearchParams();
    if (filters.loteId) params.append('loteId', filters.loteId);
    if (filters.classificacao) params.append('classificacao', filters.classificacao);
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.sendMethod) params.append('sendMethod', filters.sendMethod);
    if (filters.search) params.append('search', filters.search);
    if (filters.campoData) params.append('campoData', filters.campoData);
    if (filters.dataInicio) params.append('dataInicio', filters.dataInicio);
    if (filters.dataFim) params.append('dataFim', filters.dataFim);
    if (filters.hasNegotiation) params.append('hasNegotiation', 'true');
    if (filters.onlyDelivered) params.append('onlyDelivered', 'true');
    if (filters.onlySent) params.append('onlySent', 'true');
    if (filters.onlyWarning) params.append('onlyWarning', 'true');
    if (filters.onlyFailed) params.append('onlyFailed', 'true');
    if (filters.onlyArchived) params.append('onlyArchived', 'true');
    if (filters.hasManifestation) params.append('hasManifestation', 'true');
    if (filters.page) params.append('page', String(filters.page));
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize));
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const url = `/extrajudicial/notificacoes/itens${params.toString() ? `?${params}` : ''}`;
    const response = await apiGet(url);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao listar notificações');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  obterNotificacoesStats: async (filters: { search?: string }) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);

    const url = `/extrajudicial/notificacoes/itens/stats${params.toString() ? `?${params}` : ''}`;
    const response = await apiGet(url);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter estatísticas de notificações');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  obterLoteTabCounts: async (filters: {
    loteId: string;
    search?: string;
  }): Promise<LoteTabCounts> => {
    const params = new URLSearchParams();
    params.append('loteId', filters.loteId);
    if (filters.search) params.append('search', filters.search);

    const url = `/extrajudicial/notificacoes/itens/tab-counts?${params}`;
    const response = await apiGet(url);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter contagens das abas do lote');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  obterItem: async (
    numero: string,
    signal?: AbortSignal,
  ): Promise<ItemDetalheNotificacaoResponse> => {
    const encodedNumero = encodeURIComponent(numero);
    const response = await apiGet(
      `/extrajudicial/notificacoes/itens/numero/${encodedNumero}`,
      undefined,
      false,
      signal,
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter item');
    }
    const body = await response.json();
    const raw = body?.data ?? body;
    return mapItemManifestacoes(raw);
  },

  obterItemPorId: async (id: string): Promise<ItemDetalheNotificacaoResponse> => {
    const response = await apiGet(`/extrajudicial/notificacoes/itens/${id}`);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter item pelo ID');
    }
    const body = await response.json();
    const raw = body?.data ?? body;
    return mapItemManifestacoes(raw);
  },

  obterSnapshotEnvioDigital: async (envioId: string): Promise<EnvioDigitalSnapshotOutput> => {
    const response = await apiGet(
      `/extrajudicial/notificacoes/itens/envios-digitais/${envioId}/snapshot`,
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter snapshot do envio digital');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  enviarDigital: async (
    itemId: string,
    input: SendDigitalInput,
  ): Promise<{ success: boolean; messageId: string }> => {
    const response = await apiPost(
      `/extrajudicial/notificacoes/itens/${itemId}/enviar-digital`,
      input,
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao enviar digitalmente');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  enviarFisico: async (
    itemId: string,
    input: SendFisicoInput,
  ): Promise<{ success: boolean; messageId: string }> => {
    const response = await apiPost(
      `/extrajudicial/notificacoes/itens/${itemId}/enviar-fisico`,
      input,
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao enviar fisicamente');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  reenviarItem: async (itemId: string): Promise<{ success: boolean }> => {
    const response = await apiPost(`/extrajudicial/notificacoes/itens/${itemId}/reenviar`, {});
    if (!response.ok) {
      await handleApiError(response, 'Erro ao reenviar notificação');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  reenviarLote: async (
    loteId: string,
    input?: { apenasComFalha?: boolean; itemIds?: string[] },
  ): Promise<{ count: number }> => {
    const response = await apiPost(
      `/extrajudicial/notificacoes/lotes/${loteId}/reenviar`,
      input ?? {},
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao reenviar lote');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  // ── Central de Ajustes ──────────────────────────────────────────────
  listarAjustes: async (loteId?: string): Promise<AjusteLoteGrupo[]> => {
    const url = loteId
      ? `/extrajudicial/notificacoes/ajustes?loteId=${encodeURIComponent(loteId)}`
      : '/extrajudicial/notificacoes/ajustes';
    const response = await apiGet(url);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao carregar os ajustes');
    }
    const body = await response.json();
    const data = body?.data ?? body;
    return mapAjusteGrupos(data?.grupos);
  },

  reenviarAjuste: async (itemId: string): Promise<ReenviarAjusteResult> => {
    const response = await apiPost(
      `/extrajudicial/notificacoes/itens/${itemId}/reenviar-ajuste`,
      {},
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao reenviar notificação');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  corrigirContatosAjuste: async (itemId: string, correcoes: CorrecaoContato[]): Promise<void> => {
    const response = await apiPatch(`/extrajudicial/notificacoes/itens/${itemId}/contato`, {
      correcoes,
    });
    if (!response.ok) {
      await handleApiError(response, 'Erro ao salvar o contato');
    }
  },

  ignorarAjuste: async (itemId: string): Promise<void> => {
    const response = await apiPost(`/extrajudicial/notificacoes/itens/${itemId}/ignorar`, {});
    if (!response.ok) {
      await handleApiError(response, 'Erro ao ignorar o contato');
    }
  },

  reverterAjuste: async (itemId: string): Promise<void> => {
    const response = await apiPost(`/extrajudicial/notificacoes/itens/${itemId}/reverter`, {});
    if (!response.ok) {
      await handleApiError(response, 'Erro ao reverter o contato');
    }
  },

  downloadErrorsCsv: async (loteId: string): Promise<void> => {
    const filename = `erros_${loteId}.csv`;
    await downloadBlob(`/extrajudicial/notificacoes/lotes/${loteId}/errors.csv`, filename);
  },

  agendarLote: async (
    loteId: string,
  ): Promise<{ success: boolean; scheduledAt: string; sendAfter: string }> => {
    const response = await apiPost(`/extrajudicial/notificacoes/lotes/${loteId}/agendar`, {});
    if (!response.ok) {
      await handleApiError(response, 'Erro ao agendar lote');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  confirmarEnvio: async (
    loteId: string,
    input: {
      mode: 'now' | 'schedule';
      scheduledDate?: string;
      scheduledTime?: string;
      recomendacaoAceita?: boolean;
      negociacaoCasoId?: string;
    },
  ): Promise<{
    success: boolean;
    message?: string;
    scheduledAt?: string;
    sendAfter?: string;
    custos?: {
      custoDigital: number;
      custoFisico: number;
      custoTotal: number;
      quantidadeDigital: number;
      quantidadeFisico: number;
    };
  }> => {
    const response = await apiPost(
      `/extrajudicial/notificacoes/lotes/${loteId}/confirmar-envio`,
      input,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error || 'Erro ao confirmar envio');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  validarParaEnvio: async (
    loteId: string,
  ): Promise<{
    canConfirm: boolean;
    errors: string[];
    custos?: {
      custoDigital: number;
      custoFisico: number;
      custoTotal: number;
      quantidadeDigital: number;
      quantidadeFisico: number;
    };
  }> => {
    const response = await apiGet(`/extrajudicial/notificacoes/lotes/${loteId}/validar-envio`);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao validar lote');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  calcularCustos: async (
    loteId: string,
  ): Promise<{
    custoDigital: number;
    custoFisico: number;
    custoTotal: number;
    quantidadeDigital: number;
    quantidadeFisico: number;
  }> => {
    const response = await apiGet(`/extrajudicial/notificacoes/lotes/${loteId}/custos`);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao calcular custos');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  cancelarLote: async (
    loteId: string,
    motivo?: string,
  ): Promise<{ success: boolean; message: string; canceledAt?: string }> => {
    const response = await apiPost(`/extrajudicial/notificacoes/lotes/${loteId}/cancelar`, {
      motivo,
    });
    if (!response.ok) {
      await handleApiError(response, 'Erro ao cancelar lote');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  enviarLoteAgora: async (loteId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiPost(`/extrajudicial/notificacoes/lotes/${loteId}/enviar-agora`, {});
    if (!response.ok) {
      await handleApiError(response, 'Erro ao enviar lote');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  obterTempoRestanteLote: async (
    loteId: string,
  ): Promise<{
    secondsRemaining: number;
    canCancel: boolean;
    canEdit: boolean;
    sendAfter: string | null;
    status: string;
  }> => {
    const response = await apiGet(`/extrajudicial/notificacoes/lotes/${loteId}/tempo-restante`);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter tempo restante');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  cancelarItem: async (
    itemId: string,
    motivo?: string,
  ): Promise<{ success: boolean; message: string; canceledAt?: string; cancelType?: string }> => {
    const response = await apiPost(`/extrajudicial/notificacoes/itens/${itemId}/cancelar`, {
      motivo,
    });
    if (!response.ok) {
      await handleApiError(response, 'Erro ao cancelar item');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  encerrarItem: async (
    itemId: string,
    tipo: 'CONCLUIR' | 'CANCELAR',
    motivo?: string,
  ): Promise<{ statusGeral: string }> => {
    const response = await apiPost(`/extrajudicial/notificacoes/itens/${itemId}/encerrar`, {
      tipo,
      motivo,
    });
    if (!response.ok) {
      await handleApiError(response, 'Erro ao encerrar notificação');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  arquivarItem: async (itemId: string): Promise<{ success: boolean }> => {
    const response = await apiPost(`/extrajudicial/notificacoes/itens/${itemId}/arquivar`, {});
    if (!response.ok) {
      await handleApiError(response, 'Erro ao arquivar item');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  restaurarItem: async (itemId: string): Promise<{ success: boolean }> => {
    const response = await apiPost(`/extrajudicial/notificacoes/itens/${itemId}/restaurar`, {});
    if (!response.ok) {
      await handleApiError(response, 'Erro ao restaurar item');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  abrirNegociacao: async (
    itemId: string,
    params?: { title?: string; flowId?: string; objective?: string },
  ): Promise<void> => {
    const response = await apiPost(
      `/extrajudicial/notificacoes/itens/${itemId}/negociacao`,
      params ?? {},
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao iniciar negociação');
    }
  },

  abrirNegociacaoEmLote: async (params: {
    itemIds: string[];
    title?: string;
    flowId?: string;
  }): Promise<StartNegociacaoBulkResult> => {
    const response = await apiPost('/extrajudicial/notificacoes/itens/negociacao/bulk', params);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao iniciar negociações em lote');
    }
    const body = await response.json();
    return (body?.data ?? body) as StartNegociacaoBulkResult;
  },

  obterInfoCancelamentoItem: async (
    itemId: string,
  ): Promise<{
    secondsRemaining: number;
    canCancel: boolean;
    canEdit: boolean;
    sendAfter: string | null;
    status: string;
    cancelType?: string;
  }> => {
    const response = await apiGet(`/extrajudicial/notificacoes/itens/${itemId}/cancel-info`);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter info de cancelamento');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  obterValoresEfetivos: async (): Promise<ValoresEfetivos> => {
    const response = await apiGet('/extrajudicial/valores/efetivos');
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter valores efetivos');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  calcularCustosLote: async (loteId: string): Promise<LoteCustos> => {
    const response = await apiGet(`/extrajudicial/notificacoes/lotes/${loteId}/custos`);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao calcular custos');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  obterItemParaEdicao: async (itemId: string): Promise<ItemEditData> => {
    const response = await apiGet(`/extrajudicial/notificacoes/itens/${itemId}/edit`);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter item para edição');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  atualizarItem: async (itemId: string, input: ItemUpdateInput): Promise<ItemUpdateResult> => {
    const response = await apiPut(`/extrajudicial/notificacoes/itens/${itemId}`, input);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        success: false,
        errors: body?.errors,
        fieldErrors: body?.fieldErrors,
      };
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  obterRecomendacaoIA: async (loteId: string): Promise<RecomendacaoIA> => {
    const response = await apiGet(`/extrajudicial/notificacoes/lotes/${loteId}/recomendacao-ia`);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter recomendação da IA');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  logUploadView: async (loteId: string): Promise<void> => {
    await apiPost(`/extrajudicial/notificacoes/lotes/${loteId}/log-upload-view`, {});
  },

  logConfirmationView: async (loteId: string): Promise<void> => {
    await apiPost(`/extrajudicial/notificacoes/lotes/${loteId}/log-confirmation-view`, {});
  },

  validarCpfCnpj: async (
    doc: string,
  ): Promise<{
    tipo?: 'CPF' | 'CNPJ';
    documento?: string;
    nome: string;
    fonte?: 'database' | 'api';
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
  }> => {
    const response = await apiPost('/integracoes/cpf-cnpj/validar', { documento: doc });
    if (!response.ok) {
      await handleApiError(response, 'Erro ao validar documento');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  validarDocumentoIndividual: async (
    cpfCnpj: string,
    nome: string,
  ): Promise<{
    valido: boolean;
    erros: string[];
    nomeOficial: string | null;
    pessoaExistente: { id: string; nome: string } | null;
  }> => {
    const response = await apiPost('/extrajudicial/notificacoes/individuais/validar-documento', {
      cpfCnpj,
      nome,
    });
    if (!response.ok) {
      await handleApiError(response, 'Erro ao validar documento');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  arquivarRespostaNotificacao: async (
    itemId: string,
    respostaId: string,
  ): Promise<{ success: boolean }> => {
    const response = await apiPatch(
      `/extrajudicial/notificacoes/${itemId}/resposta/${respostaId}/arquivar`,
      {},
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao arquivar resposta');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  uploadNotificacaoAnexos: async (itemId: string, files: File[]): Promise<void> => {
    if (files.length === 0) return;
    const form = new FormData();
    files.forEach((file) => form.append('arquivos', file, file.name));

    const response = await apiPost(`/extrajudicial/notificacoes/itens/${itemId}/anexos`, form);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao enviar anexos da notificação');
    }
  },

  baixarAnexoResposta: async (itemId: string, respostaId: string): Promise<Blob> => {
    const response = await apiGet(
      `/extrajudicial/notificacoes/${itemId}/resposta/${respostaId}/anexo`,
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao baixar anexo');
    }
    return await response.blob();
  },

  obterManifestacaoAnexoUrl: async (
    itemId: string,
    manifestacaoId: string,
    anexoIndex: number,
  ): Promise<{ url: string; name: string }> => {
    const response = await apiGet(
      `/extrajudicial/notificacoes/itens/${itemId}/manifestacoes/${manifestacaoId}/anexo/${anexoIndex}`,
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter URL do anexo');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  getGeneralDashboard: async (filters: DashboardFilters): Promise<GeneralDashboardResponse> => {
    const params = buildDashboardParams(filters);

    const response = await apiGet(
      `/extrajudicial/dashboard/notifications/general?${params.toString()}`,
    );

    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter dashboard geral');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  getChannelDashboard: async (filters: DashboardFilters): Promise<ChannelDashboardResponse> => {
    const params = buildDashboardParams(filters);

    const response = await apiGet(
      `/extrajudicial/dashboard/notifications/channel?${params.toString()}`,
    );

    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter dashboard dos canais');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  getManifestationDashboard: async (
    filters: DashboardFilters,
  ): Promise<ManifestationDashboardResponse> => {
    const params = buildDashboardParams(filters);

    const response = await apiGet(
      `/extrajudicial/dashboard/notifications/manifestation?${params.toString()}`,
    );

    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter dashboard das manifestações');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  getBatchDashboard: async (filters: DashboardFilters): Promise<BatchDashboardResponse> => {
    const params = buildDashboardParams(filters);

    const response = await apiGet(
      `/extrajudicial/dashboard/notifications/batch?${params.toString()}`,
    );

    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter dashboard dos lotes');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  obterLotePerformance: async (loteId: string): Promise<LotePerformanceDetailedResponse> => {
    const response = await apiGet(`/extrajudicial/notificacoes/lotes/${loteId}/performance`);
    if (!response.ok) {
      await handleApiError(response, 'Erro ao obter performance do lote');
    }
    const body = await response.json();
    return body?.data ?? body;
  },

  obterManifestacaoChat: async (
    itemId: string,
    consulta: ConsultaDaManifestacaoChat,
    signal?: AbortSignal,
  ): Promise<LeituraDaPaginaDaManifestacao> => {
    const response = await apiGet(
      `/extrajudicial/notificacoes/itens/${encodeURIComponent(itemId)}/manifestacao/mensagens`,
      { page: consulta.page },
      false,
      signal,
    ).catch((erro: unknown) => {
      if (signal?.aborted || (isRecord(erro) && erro.name === 'AbortError')) throw erro;
      throw new FalhaDaConsultaDaManifestacao(0);
    });
    // O corpo da recusa pode ecoar contato ou conteúdo jurídico; só o status atravessa a fronteira.
    if (!response.ok) throw new FalhaDaConsultaDaManifestacao(response.status);
    return lerPaginaDaManifestacao(await lerCorpoDaManifestacao(response));
  },

  enviarRespostaSimples: async (
    itemId: string,
    conteudo: string,
  ): Promise<LeituraDaMensagemDaManifestacao> => {
    const response = await apiPost(
      `/extrajudicial/notificacoes/itens/${encodeURIComponent(itemId)}/manifestacao/resposta-simples`,
      { conteudo },
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao enviar resposta simples');
    }
    const foraDoContrato: MensagemForaDoContrato[] = [];
    const mensagem = lerMensagemDaManifestacao(
      await lerCorpoDaManifestacao(response),
      foraDoContrato,
    );
    return { mensagem, foraDoContrato };
  },

  marcarManifestacaoLida: async (itemId: string): Promise<{ marcadas: number }> => {
    const response = await apiPatch(
      `/extrajudicial/notificacoes/itens/${itemId}/manifestacao/marcar-lidas`,
      {},
    );
    if (!response.ok) {
      await handleApiError(response, 'Erro ao marcar manifestação como lida');
    }
    const body = await response.json();
    return body?.data ?? body;
  },
};
